import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ICDSSalesAfsoReport from "./ICDSSalesAfsoReport";

const ICDSSalesReport = ({
  month,
  year,
  allotMonth,
  allotYear,
  distMonthName,
  alotMonthName,
}) => {
  const [submitMessage, setSubmitMessage] = useState("");
  const [loading, setLoading] = useState([]);
  const [icdsCommodityData, setICDSCommodityData] = useState([]);
  const [icdsDistrictSalesData, setICDSDistrictSalesData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [commodityRes, districtRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_URL}/api/icds/getICDSCommodities?month=${month}&year=${year}&alotmonth=${allotMonth}&alotyear=${allotYear}`
        ),
        fetch(
          `${process.env.REACT_APP_URL}/api/icds/getDistrictWiseICDSDetails?month=${month}&year=${year}&alotMonth=${allotMonth}&alotYear=${allotYear}`
        ),
      ]);

      if (!commodityRes.ok) throw new Error("Failed to fetch ICDS commodities");
      if (!districtRes.ok) throw new Error("Failed to fetch district sales");

      const commodityData = await commodityRes.json();
      const districtData = await districtRes.json();

      setICDSCommodityData(commodityData);
      setICDSDistrictSalesData(districtData);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year, allotMonth, allotYear]);

  const totals = icdsDistrictSalesData.reduce(
    (acc, item) => {
      acc.totalFps += Number(item.total_fps) || 0;
      acc.totalSchools += Number(item.total_school) || 0;

      icdsCommodityData.forEach((commodity) => {
        const availedKey = `availed_aws_comm_${commodity.commId}`;
        const qtyKey = `total_availed_qty_comm_${commodity.commId}`;

        if (!acc.commodities) acc.commodities = {};
        if (!acc.commodities[commodity.commId]) {
          acc.commodities[commodity.commId] = { totalAvailed: 0, totalQty: 0 };
        }

        acc.commodities[commodity.commId].totalAvailed +=
          Number(item[availedKey]) || 0;
        acc.commodities[commodity.commId].totalQty += Number(item[qtyKey]) || 0;
      });

      acc.count += 1;

      return acc;
    },
    {
      totalFps: 0,
      totalSchools: 0,
      commodities: {},
      count: 0,
    }
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  const handleDownloadExcel = async (
    districtData,
    commodityData,
    totals,
    distMonthName,
    year,
    allotMonthName,
    allotYear
  ) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("ICDS Sales Report");

    // === Set Column Widths ===
    const columnWidths = [
      8, // Sl No
      22, // District
      12, // FPS
      12, // AWS
    ];

    // Commodity columns (2 per commodity)
    commodityData.forEach(() => {
      columnWidths.push(15); // Availed AWS
      columnWidths.push(20); // Availed Qty
    });

    worksheet.columns = columnWidths.map((width) => ({ width }));

    // === Calculate total columns dynamically ===
    const totalColumns = 4 + commodityData.length * 2; // SlNo + District + Total(FPS+AWS) + commodity sale columns

    // === Title Row ===
    worksheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = `ICDS Sales for Distribution (Month'Year): ${distMonthName}'${year} and Allotment (Month'Year): ${allotMonthName}'${allotYear}`;
    titleCell.font = { size: 12, bold: true };
    titleCell.alignment = { horizontal: "center" };

    // === Header Rows ===
    // Row 2: Sl No, District, Total columns merged
    worksheet.mergeCells(2, 1, 4, 1); // Sl No
    worksheet.getCell("A2").value = "Sl No";

    worksheet.mergeCells(2, 2, 4, 2); // District
    worksheet.getCell("B2").value = "District";

    worksheet.mergeCells(2, 3, 3, 4); // Total (FPS, AWS, Entitlement)
    worksheet.getCell("C2").value = "Total";

    // Commodity Sale Header
    worksheet.mergeCells(2, 5, 2, totalColumns);
    worksheet.getCell("F2").value = "Sale";

    // Row 3: Commodity Names
    let col = 5;
    commodityData.forEach((commodity) => {
      worksheet.mergeCells(3, col, 3, col + 1);
      worksheet.getCell(3, col).value = commodity.commName;
      col += 2;
    });

    // Row 4: Subheaders
    worksheet.getCell("C4").value = "FPS";
    worksheet.getCell("D4").value = "AWS";
    // worksheet.getCell("E4").value = "Entitlement";

    col = 5;
    commodityData.forEach(() => {
      worksheet.getCell(4, col).value = "Availed AWS";
      worksheet.getCell(4, col + 1).value = "Availed Qty (Kgs/Lts)";
      col += 2;
    });

    // Apply header styles
    [2, 3, 4].forEach((rowNumber) => {
      const row = worksheet.getRow(rowNumber);
      row.font = { bold: true };
      row.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFDDDDDD" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // === Data Rows ===
    districtData.forEach((item, index) => {
      const rowValues = [
        index + 1,
        item.dist_name_en,
        item.total_fps,
        item.total_school,
      ];

      commodityData.forEach((commodity) => {
        rowValues.push(item[`availed_aws_comm_${commodity.commId}`]);
        rowValues.push(
          item[`total_availed_qty_comm_${commodity.commId}`].toFixed(3)
        );
      });

      const row = worksheet.addRow(rowValues);

      // Align numeric columns
      for (let i = 3; i <= rowValues.length; i++) {
        row.getCell(i).alignment = { horizontal: "right" };
      }
    });

    // === Total Row ===
    const totalRowValues = [
      "",
      "Total",
      totals.totalFps,
      totals.totalSchools,
    ];

    commodityData.forEach((commodity) => {
      totalRowValues.push(
        totals.commodities[commodity.commId]?.totalAvailed || 0
      );
      totalRowValues.push(
        (totals.commodities[commodity.commId]?.totalQty || 0).toFixed(3)
      );
    });

    const totalRow = worksheet.addRow(totalRowValues);
    totalRow.font = { bold: true };
    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };
      cell.alignment =
        colNumber <= 2 ? { horizontal: "left" } : { horizontal: "right" };
    });

    // === Download Excel ===
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `ICDS_District_Sales_Report.xlsx`);
  };

  const handleDistClick = (dist_code, dist_name_en) => {
    setSelectedDistrict({
      distCode: dist_code,
      distName: dist_name_en,
    });
  };

  return (
    <>
      {icdsCommodityData.length > 0 ? (
        <div style={{ justifyContent: "center", marginBottom: "70px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "90%",
              margin: "0 auto 20px auto",
            }}
          >
            {<div style={{ width: "150px" }}></div>}

            <table width="90%" border="1" cellPadding="5" cellSpacing="0">
              <thead style={{ position: "sticky", top: 0 }}>
                <tr className="tableheader">
                  <th colSpan={8 + icdsCommodityData.length * 4}>
                    ICDS Sales for Distribution (Month'Year) : {distMonthName}'
                    {year} and Allotment (Month'Year) : {alotMonthName}'
                    {allotYear}
                  </th>
                </tr>
                <tr className="tableheader">
                  <th rowSpan={3}>Sl No</th>
                  <th rowSpan={3}>District</th>
                  <th rowSpan={2} colSpan={2}>
                    Total
                  </th>
                  <th colSpan={icdsCommodityData.length * 2}>Sale</th>
                </tr>

                {/* Commodity Names */}
                <tr className="tableheader">
                  {icdsCommodityData.map((commodity, index) => (
                    <th key={index} colSpan={2}>
                      {commodity.commName}
                    </th>
                  ))}
                </tr>

                <tr className="tableheader">
                  <th>FPS</th>
                  <th>AWS</th>

                  {icdsCommodityData.map((commodity, index) => (
                    <React.Fragment key={index}>
                      <th>
                        Availed
                        <br />
                        AWS
                      </th>
                      <th>
                        Availed
                        <br />
                        Qty
                        <br />
                        (Kgs/Lts)
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {icdsDistrictSalesData.map((item, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                    <td>
                      <a
                        style={{ cursor: "pointer", color: "blue" }}
                        onClick={() =>
                          handleDistClick(item.dist_code, item.dist_name_en)
                        }
                      >
                        {item.dist_name_en}
                      </a>
                    </td>
                    <td style={{ textAlign: "right" }}>{item.total_fps}</td>
                    <td style={{ textAlign: "right" }}>{item.total_school}</td>
   
                    {icdsCommodityData.map((commodity) => (
                      <React.Fragment key={commodity.commId}>
                        <td style={{ textAlign: "right" }}>
                          {item[`availed_aws_comm_${commodity.commId}`]}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {item[
                            `total_availed_qty_comm_${commodity.commId}`
                          ].toFixed(3)}
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: "bold" }} className="tableheader">
                  <td colSpan={2} style={{ textAlign: "center" }}>
                    Total
                  </td>
                  <td style={{ textAlign: "right" }}>{totals.totalFps}</td>
                  <td style={{ textAlign: "right" }}>{totals.totalSchools}</td>
               
                  {icdsCommodityData.map((commodity) => (
                    <React.Fragment key={commodity.commId}>
                      <td style={{ textAlign: "right" }}>
                        {totals.commodities[commodity.commId]?.totalAvailed}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {totals.commodities[commodity.commId]?.totalQty.toFixed(
                          3
                        )}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            {
              <button
                onClick={() =>
                  handleDownloadExcel(
                    icdsDistrictSalesData,
                    icdsCommodityData,
                    totals,
                    distMonthName,
                    year,
                    alotMonthName,
                    allotYear
                  )
                }
                className="button button2"
              >
                Download Excel
              </button>
            }
          </div>
          <br/>
          {selectedDistrict && (
            <ICDSSalesAfsoReport
              key={selectedDistrict.distCode} 
              month={month}
              year={year}
              allotMonth={allotMonth}
              allotYear={allotYear}
              distMonthName={distMonthName}
              alotMonthName={alotMonthName}
              distCode={selectedDistrict.distCode}
              icdsCommodityData={icdsCommodityData}
              distName={selectedDistrict.distName}
            />
          )}
        </div>
      ) : (
        <div className="mainhead" align="center">
          {" "}
          No data found{" "}
        </div>
      )}

      <div id="loading" style={{ display: "none", textAlign: "center" }}>
        <img src="images/progress.gif" alt="Loading" />
      </div>   
    </>
  );
};

export default ICDSSalesReport;
